/**
 * Product Image Utilities
 * Provides consistent image resolution logic across marketplace and detail views
 */

/**
 * getPrimaryProductImage - Resolve a single primary image URL for a product
 * 
 * Priority order:
 * 1. primary_image_url (direct URL)
 * 2. First item in image_gallery array (if available)
 * 3. First item in images array (if available)
 * 4. null (fallback to placeholder)
 * 
 * @param {Object} product - Product object from API or Supabase
 * @returns {string|null} Primary image URL or null if no image found
 */
export function getPrimaryProductImage(product) {
  if (!product) return null;

  // Preference 1: primary_image_url (usually main_image_url)
  if (product.primary_image_url) {
    return product.primary_image_url;
  }
  if (product.main_image_url) {
    return product.main_image_url;
  }

  // Preference 2: First image in image_gallery array
  if (product.image_gallery) {
    let gallery = product.image_gallery;

    // Parse JSON string if needed
    if (typeof gallery === "string") {
      try {
        gallery = JSON.parse(gallery);
      } catch (err) {
        if (process.env.NODE_ENV === "development") {
          console.warn(
            `[productImageUtils] Failed to parse image_gallery for product ${product.id}:`,
            err
          );
        }
        gallery = [];
      }
    }

    // Extract first image URL
    if (Array.isArray(gallery) && gallery.length > 0) {
      const firstGalleryItem = gallery[0];
      if (typeof firstGalleryItem === "string") {
        return firstGalleryItem;
      } else if (
        typeof firstGalleryItem === "object" &&
        firstGalleryItem !== null
      ) {
        return firstGalleryItem.url || firstGalleryItem.publicUrl || null;
      }
    }
  }

  // Preference 3: First image in images array (alternate field)
  if (product.images) {
    let images = product.images;

    // Parse JSON string if needed
    if (typeof images === "string") {
      try {
        images = JSON.parse(images);
      } catch (err) {
        if (process.env.NODE_ENV === "development") {
          console.warn(
            `[productImageUtils] Failed to parse images for product ${product.id}:`,
            err
          );
        }
        images = [];
      }
    }

    // Extract first image URL
    if (Array.isArray(images) && images.length > 0) {
      const firstImage = images[0];
      if (typeof firstImage === "string") {
        return firstImage;
      } else if (typeof firstImage === "object" && firstImage !== null) {
        return firstImage.url || firstImage.publicUrl || null;
      }
    }
  }

  // No image found
  return null;
}

/**
 * getAllProductImages - Extract all image URLs from a product for carousel
 * 
 * Combines all available image sources (main + gallery + images array)
 * 
 * @param {Object} product - Product object from API or Supabase
 * @returns {string[]} Array of unique image URLs
 */
export function getAllProductImages(product) {
  if (!product) return [];

  const imageSet = new Set();

  // Add main_image_url
  if (product.main_image_url) {
    imageSet.add(product.main_image_url);
  }
  if (product.primary_image_url) {
    imageSet.add(product.primary_image_url);
  }

  // Add image_gallery items
  if (product.image_gallery) {
    let gallery = product.image_gallery;

    // Parse JSON string if needed
    if (typeof gallery === "string") {
      try {
        gallery = JSON.parse(gallery);
      } catch (err) {
        if (process.env.NODE_ENV === "development") {
          console.warn(
            `[productImageUtils] Failed to parse image_gallery for product ${product.id}:`,
            err
          );
        }
        gallery = [];
      }
    }

    // Add each gallery image
    if (Array.isArray(gallery)) {
      gallery.forEach((item) => {
        if (typeof item === "string") {
          imageSet.add(item);
        } else if (typeof item === "object" && item !== null) {
          const url = item.url || item.publicUrl;
          if (url) imageSet.add(url);
        }
      });
    }
  }

  // Add images array items (alternate field)
  if (product.images) {
    let images = product.images;

    // Parse JSON string if needed
    if (typeof images === "string") {
      try {
        images = JSON.parse(images);
      } catch (err) {
        if (process.env.NODE_ENV === "development") {
          console.warn(
            `[productImageUtils] Failed to parse images for product ${product.id}:`,
            err
          );
        }
        images = [];
      }
    }

    // Add each image
    if (Array.isArray(images)) {
      images.forEach((item) => {
        if (typeof item === "string") {
          imageSet.add(item);
        } else if (typeof item === "object" && item !== null) {
          const url = item.url || item.publicUrl;
          if (url) imageSet.add(url);
        }
      });
    }
  }

  return Array.from(imageSet);
}

/**
 * resolveStorageImageUrl - Convert Supabase storage paths to public URLs
 * 
 * If the image URL is already an HTTP(S) URL, returns it as-is.
 * If it's a storage path, converts it to a public URL using Supabase Storage API.
 * 
 * This is used by components to resolve image paths from the database.
 * 
 * @param {string} imageUrl - Image URL or storage path
 * @param {Object} supabaseClient - Supabase client instance
 * @param {string} bucketName - Storage bucket name (default: 'marketplace-media')
 * @returns {string} Public URL or original URL if conversion fails
 */
export function resolveStorageImageUrl(
  imageUrl,
  supabaseClient,
  bucketName = "marketplace-media"
) {
  if (!imageUrl) return null;

  // Already an HTTP URL, return as-is
  if (typeof imageUrl === "string" && imageUrl.startsWith("http")) {
    return imageUrl;
  }

  // Try to convert Supabase storage path to public URL
  if (supabaseClient) {
    try {
      const { data } = supabaseClient.storage
        .from(bucketName)
        .getPublicUrl(imageUrl);
      return data?.publicUrl || imageUrl;
    } catch (err) {
      if (process.env.NODE_ENV === "development") {
        console.warn(
          `[productImageUtils] Failed to resolve storage URL: ${imageUrl}`,
          err
        );
      }
      return imageUrl;
    }
  }

  return imageUrl;
}
